import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Headset } from "@phosphor-icons/react";
import { MaskedLines, Reveal } from "@/components/Motion";
import { SERVICES } from "@/data/content";

const IMAGES = {
  sourcing: "https://images.pexels.com/photos/34054493/pexels-photo-34054493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  trading: "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxnbG9iYWwlMjBzdXBwbHklMjBjaGFpbiUyMGxvZ2lzdGljcyUyMGNvbnRhaW5lcnxlbnwwfHx8fDE3ODQ5NTAyMzF8MA&ixlib=rb-4.1.0&q=85",
  operations: "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Services = () => (
  <div data-testid="page-services">
    <section className="bg-[#050a11] text-white pt-40 pb-24 md:pb-28">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-[#9dc0ff] mb-8">Services</p>
        <h1 className="font-display font-light text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tight max-w-4xl">
          <MaskedLines lines={["Three services.", "One seamless chain."]} />
        </h1>
        <p className="mt-10 max-w-xl text-white/60 text-lg leading-relaxed">
          Engage us for a single stage or the entire journey — sourcing, trading and operations, coordinated by one dedicated team.
        </p>
      </div>
    </section>

    <section className="bg-[#f8f9fa] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 space-y-28 md:space-y-40">
        {SERVICES.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <div
              id={s.id}
              key={s.id}
              data-testid={`service-detail-${s.id}`}
              className="grid md:grid-cols-12 gap-10 md:gap-16 items-center scroll-mt-32"
            >
              <Reveal className={`md:col-span-6 ${reversed ? "md:order-2" : ""}`}>
                <div className="overflow-hidden border border-black/10 relative">
                  <img src={IMAGES[s.id]} alt={s.title} className="w-full h-[46vh] object-cover photo-grayscale" />
                  <span className="absolute top-5 left-5 font-mono-x text-xs bg-white/90 text-[#0b2046] px-2.5 py-1">
                    {s.no}
                  </span>
                </div>
              </Reveal>

              <Reveal delay={0.12} className={`md:col-span-6 ${reversed ? "md:order-1" : ""}`}>
                <h2 className="font-display font-light text-4xl md:text-5xl leading-none tracking-tight text-[#050a11]">
                  {s.title}
                </h2>
                <p className="mt-6 text-[#4b5563] text-lg leading-relaxed max-w-lg">{s.body}</p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-y-3 gap-x-6">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-[#050a11]">
                      <Check size={18} weight="bold" className="text-[#0b2046] mt-1 shrink-0" />
                      <span className="text-sm">{p}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          );
        })}
      </div>
    </section>

    {/* dedicated support badge / CTA */}
    <section data-testid="services-support" className="bg-[#0b2046] text-white py-24 md:py-28">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <div className="flex items-center gap-3 mb-6">
            <Headset size={28} weight="duotone" className="text-[#9dc0ff]" />
            <span className="overline text-[#9dc0ff]">Dedicated support team</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-light text-4xl md:text-5xl leading-tight tracking-tight max-w-2xl"
          >
            A single point of contact, accountable for your entire operation.
          </motion.h2>
        </div>
        <div className="md:col-span-4 md:text-right">
          <Link
            to="/contact"
            data-testid="services-cta"
            className="group inline-flex items-center gap-3 bg-white text-[#0b2046] font-medium rounded-full pl-7 pr-5 py-4 hover:bg-[#9dc0ff] active:scale-[0.98] transition-[background-color,transform]"
          >
            Talk to our team
            <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
